'use client';

/**
 * EmailVerifyBanner — toast popup chiudibile (top-right) per user con
 * email_verified=false. Bloccato dal compiere operazioni (booking,
 * profile update, ambassador apply, ecc.) finché non clicca link
 * verifica ricevuto via email al register.
 *
 * Anti-spam policy:
 *  - 24h max tempo per confermare (oltre user viene auto-cancellato)
 *  - 3 tentativi con stessa email/phone = ban (vedi /api/auth/register)
 *
 * Resend disponibile: 1 click → POST /api/auth/resend-verification.
 *
 * Dismiss: sessionStorage flag — riappare al prossimo refresh (intenzionale:
 * gate backend è attivo, user deve essere ricordato finché non verifica).
 */

import { useEffect, useState } from 'react';
import { Mail, AlertCircle, RefreshCw, Check, X } from 'lucide-react';
import { useAuth } from '@/app/providers';

const DISMISS_KEY = 'insula-email-verify-dismissed';

export default function EmailVerifyBanner() {
  const { user, refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(true);
  // Gate: non renderizzare finché /api/auth/me fresco non ha confermato lo stato.
  // Evita il FLASH del banner quando lo user iniziale (JWT/state) è stale rispetto
  // al DB (es. email_verified/role non ancora popolati al primo render post-login).
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Reset del gate ad ogni cambio utente (es. login): la response di login può
    // avere role/email_verified stale → nascondi finché /api/auth/me non conferma.
    setChecked(false);
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    refresh().catch(() => { /* silent */ }).finally(() => setChecked(true));
    // refresh ESCLUSO dalle deps: identità non-memoizzata da useAuth → l'effect ri-parte
    // a ogni render → setChecked(false→true) in loop → banner ri-montato e ri-animato
    // = "lampeggia". Reset gate solo al cambio utente (user?.id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Render per cliente E partner (VENDOR/COMPANY) con email NON verificata.
  // ADMIN esente (verifica via admin invite). AMBASSADOR esente. I partner ora
  // ricevono il prompt (prima erano esclusi → restavano unverified senza nudge).
  const EXEMPT_ROLES = ['ADMIN', 'AMBASSADOR'];
  if (!checked) return null; // attendi conferma fresca → niente flash
  if (!user) return null;
  if (EXEMPT_ROLES.includes(user.role)) return null;
  const isPartner = user.role === 'VENDOR' || user.role === 'COMPANY';
  // Mostra SOLO se l'email è esplicitamente NON confermata (=== false).
  // undefined/null/true (sconosciuto o verificata) → nascosto. Niente flash.
  if (user.email_verified !== false) return null;
  if (dismissed) return null;

  const close = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const resend = async () => {
    setSending(true);
    setError(null);
    setAlreadyVerified(false);
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      // Backend ritorna 200 anche per user già verificato (idempotente).
      // UI distingue: "già verificata" vs "inviata" per evitare confusione.
      if (typeof data.message === 'string' && /già verificata/i.test(data.message)) {
        setAlreadyVerified(true);
        // Forza refresh stato auth: banner sparirà al prossimo render perché
        // user.email_verified diventa true.
        refresh().catch(() => { /* silent */ });
        setTimeout(() => setAlreadyVerified(false), 8000);
      } else {
        setSent(true);
        setTimeout(() => setSent(false), 8000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore invio');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-cid="email-verify-banner"
      role="alert"
      className="fixed top-20 right-4 sm:right-6 z-[105] max-w-sm w-[calc(100vw-2rem)] sm:w-96 animate-in slide-in-from-top-4 fade-in duration-500"
    >
      <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/95 backdrop-blur-xl border border-amber-300 dark:border-amber-700/60 shadow-2xl shadow-amber-900/20 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-500 text-amber-950 flex items-center justify-center">
              <AlertCircle size={18} />
            </div>
            <div data-cid="email-verify-banner-message" className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-950 dark:text-amber-100 leading-snug">
                Verifica la tua email
              </p>
              <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mt-1 leading-relaxed break-words">
                Conferma <span className="font-mono break-all">{user.email}</span>{' '}
                {isPartner
                  ? 'per sbloccare la pubblicazione degli annunci e le operazioni partner.'
                  : 'entro 24h dalla registrazione per sbloccare prenotazioni e operazioni piattaforma.'}
              </p>
              {!isPartner && (
                <p className="text-[10px] text-amber-900/60 dark:text-amber-200/60 mt-1.5 uppercase tracking-wider">
                  Oltre 24h l&apos;account viene cancellato automaticamente
                </p>
              )}
            </div>
            <button
              type="button"
              data-cid="email-verify-banner-close"
              onClick={close}
              aria-label="Chiudi"
              className="shrink-0 p-1.5 rounded-lg text-amber-900/60 hover:text-amber-950 hover:bg-amber-200/60 dark:text-amber-200/60 dark:hover:text-amber-100 dark:hover:bg-amber-900/60 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <div data-cid="email-verify-banner-actions" className="mt-3 flex items-center gap-2">
            {alreadyVerified ? (
              <span data-cid="email-verify-banner-already" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold w-full justify-center">
                <Check size={14} /> Email già verificata
              </span>
            ) : sent ? (
              <span data-cid="email-verify-banner-sent" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold w-full justify-center">
                <Check size={14} /> Email inviata, controlla la posta
              </span>
            ) : (
              <button
                type="button"
                data-cid="email-verify-banner-resend"
                onClick={resend}
                disabled={sending}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-900 hover:bg-amber-950 text-amber-50 text-xs font-bold transition-colors disabled:opacity-50 w-full"
              >
                {sending ? <RefreshCw size={14} className="animate-spin" /> : <Mail size={14} />}
                {sending ? 'Invio…' : 'Rispedisci email di verifica'}
              </button>
            )}
          </div>

          {error && (
            <div data-cid="email-verify-banner-error" className="mt-2 text-[11px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-2.5 py-1.5 rounded-lg">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
